import { useEffect } from 'react';
import { toPng } from 'html-to-image';

/**
 * Wires the existing "Save image" button inside every LockCard to actually
 * download a 1080x1080 PNG of the card. No visible UI: we mount this once
 * inside GatedLesson and attach a document-level click handler that catches
 * clicks on any button living inside #sec-lock-card whose label says
 * "Save image".
 *
 * Capture strategy (simple and reliable):
 *   1. Capture the *actual* lock-card DOM node in place with html-to-image.
 *      Everything is already painted, styled, and visible: no cloning,
 *      no offscreen stages, no CSS-var lookups. This is the piece that has
 *      to be pixel-perfect.
 *   2. Draw that PNG into a 1080x1080 canvas, centered, with a Constare
 *      brand frame (header + footer) around it. The frame is drawn with
 *      canvas 2D primitives, not HTML, so we don't fight cross-browser
 *      DOM-rendering quirks.
 *   3. Export the canvas as a PNG and trigger the download.
 */
export default function LockCardSave({
  lessonSlug,
  lessonTitle,
}: {
  lessonSlug: string;
  lessonTitle: string;
}) {
  useEffect(() => {
    async function handleClick(ev: MouseEvent) {
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      const btn = target.closest('button');
      if (!btn) return;
      const section = btn.closest('#sec-lock-card');
      if (!section) return;
      const label = (btn.textContent || '').trim().toLowerCase();
      if (!label.includes('save image')) return;

      ev.preventDefault();
      ev.stopPropagation();

      const original = btn.textContent;
      btn.setAttribute('disabled', 'true');
      btn.textContent = 'Saving…';
      try {
        await captureLockCard({ lessonSlug, lessonTitle, section: section as HTMLElement });
      } catch (err) {
        console.error('[LockCardSave] capture failed', err);
        alert('Sorry, could not save the image. Try again in a moment.');
      } finally {
        btn.removeAttribute('disabled');
        if (original) btn.textContent = original;
      }
    }

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [lessonSlug, lessonTitle]);

  return null;
}

// Brand palette. Mirrors tailwind.config.ts.
const BRAND = {
  ink: '#0A0A0B',
  elevated: '#181820',
  muted: '#8A8A95',
  text: '#E6E6EA',
  amber: '#FFB547',
};

async function captureLockCard(opts: {
  lessonSlug: string;
  lessonTitle: string;
  section: HTMLElement;
}) {
  const { lessonSlug, lessonTitle, section } = opts;

  // Find the actual card body inside the section.
  //
  // Preferred: an explicit data attribute on the card wrapper. Every lesson
  // page's LockCard has data-lockcard-body on the inner wrapper so class
  // tweaks won't break capture.
  //
  // Fallback (for older lessons or manual overrides): data-lockcard-capture,
  // then the first tall rounded container inside the section.
  const card =
    section.querySelector<HTMLElement>('[data-lockcard-body]') ||
    section.querySelector<HTMLElement>('[data-lockcard-capture]') ||
    findLockCardBody(section);

  if (!card) throw new Error('lock-card body not found');

  // Wait for web fonts before capture.
  if (document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch {
      // ignore
    }
  }

  // Temporarily hide the "Save image" button(s) inside the card so they
  // don't appear in the exported image. Restore visibility after capture.
  const hiddenBtns: Array<{ el: HTMLElement; prev: string }> = [];
  card.querySelectorAll('button').forEach((b) => {
    if ((b.textContent || '').toLowerCase().includes('save image')) {
      hiddenBtns.push({ el: b as HTMLElement, prev: b.style.visibility });
      (b as HTMLElement).style.visibility = 'hidden';
    }
  });

  let cardPng: string;
  try {
    // Measure the card so we know its aspect ratio.
    const rect = card.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      throw new Error('lock-card has zero size');
    }

    // Capture at pixelRatio 2 for a crisp source image. The card is
    // typically ~640x400 CSS pixels, so pixelRatio 2 gives ~1280x800 which
    // we then downscale into the 1080 frame.
    cardPng = await toPng(card, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: BRAND.elevated,
    });
  } finally {
    hiddenBtns.forEach(({ el, prev }) => {
      el.style.visibility = prev;
    });
  }

  // Load the captured card into an Image so we can draw it onto a canvas.
  const cardImg = await loadImage(cardPng);

  // Build the 1080x1080 output.
  const CANVAS = 1080;
  const PAD = 72;
  const HEADER_H = 60;
  const FOOTER_H = 60;
  const INNER_TOP = PAD + HEADER_H + 24;
  const INNER_BOTTOM = CANVAS - PAD - FOOTER_H - 24;
  const INNER_LEFT = PAD;
  const INNER_RIGHT = CANVAS - PAD;
  const INNER_W = INNER_RIGHT - INNER_LEFT;
  const INNER_H = INNER_BOTTOM - INNER_TOP;

  const canvas = document.createElement('canvas');
  canvas.width = CANVAS;
  canvas.height = CANVAS;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d not supported');

  // Background.
  ctx.fillStyle = BRAND.ink;
  ctx.fillRect(0, 0, CANVAS, CANVAS);

  // Header: amber square with "C", brand wordmark, lesson tag on the right.
  const headerY = PAD;
  // Amber logo tile.
  const tileSize = 40;
  drawRoundedRect(ctx, PAD, headerY + 10, tileSize, tileSize, 8);
  ctx.fillStyle = BRAND.amber;
  ctx.fill();
  ctx.fillStyle = BRAND.ink;
  ctx.font = 'bold 22px ui-monospace, "SFMono-Regular", Menlo, monospace';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText('C', PAD + tileSize / 2, headerY + 10 + tileSize / 2 + 1);

  // Brand wordmark.
  ctx.fillStyle = BRAND.text;
  ctx.font = '500 16px ui-monospace, "SFMono-Regular", Menlo, monospace';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText('CONSTARE ACADEMY', PAD + tileSize + 18, headerY + 10 + tileSize / 2);

  // Right-side lesson tag.
  ctx.fillStyle = BRAND.amber;
  ctx.font = '500 14px ui-monospace, "SFMono-Regular", Menlo, monospace';
  ctx.textAlign = 'right';
  ctx.fillText(
    `LESSON ${lessonSlug.toUpperCase()} \u00B7 LOCK CARD`,
    CANVAS - PAD,
    headerY + 10 + tileSize / 2
  );

  // Draw the captured card centered in the inner area, preserving aspect.
  const cardAspect = cardImg.width / cardImg.height;
  let drawW: number;
  let drawH: number;
  if (cardAspect > INNER_W / INNER_H) {
    drawW = INNER_W;
    drawH = drawW / cardAspect;
  } else {
    drawH = INNER_H;
    drawW = drawH * cardAspect;
  }
  const drawX = INNER_LEFT + (INNER_W - drawW) / 2;
  const drawY = INNER_TOP + (INNER_H - drawH) / 2;

  // Rounded clip for the card area so corners are consistent with the app.
  ctx.save();
  drawRoundedRect(ctx, drawX, drawY, drawW, drawH, 20);
  ctx.clip();
  // Fill behind card with elevated color, in case card PNG has transparency
  // at the corners after rounding.
  ctx.fillStyle = BRAND.elevated;
  ctx.fillRect(drawX, drawY, drawW, drawH);
  ctx.drawImage(cardImg, drawX, drawY, drawW, drawH);
  ctx.restore();

  // Footer: lesson title on the left, URL on the right.
  const footerY = CANVAS - PAD - FOOTER_H;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.fillStyle = BRAND.text;
  ctx.font =
    '400 20px Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
  const titleText = lessonTitle;
  wrapText(ctx, titleText, PAD, footerY + 6, CANVAS - PAD * 2 - 200, 26, 2);

  ctx.textAlign = 'right';
  ctx.fillStyle = BRAND.muted;
  ctx.font = '500 14px ui-monospace, "SFMono-Regular", Menlo, monospace';
  ctx.fillText('constare.academy', CANVAS - PAD, footerY + 12);

  // Export.
  const finalUrl = canvas.toDataURL('image/png');
  const a = document.createElement('a');
  a.href = finalUrl;
  a.download = `constare-lesson-${lessonSlug}-lockcard.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Pick the visual "card" element inside a lock-card Section. We look for a
 * bordered rounded container that is either a direct child of the Section's
 * inner wrapper or the first meaningful rounded-* element we can find.
 * Matches: rounded-xl, rounded-2xl, rounded-3xl, or explicit lg variants.
 */
function findLockCardBody(section: HTMLElement): HTMLElement | null {
  // First try classic rounded card variants at any depth.
  const selectors = [
    'div.rounded-2xl',
    'div.rounded-xl',
    'div.rounded-3xl',
    'div.rounded-lg',
  ];
  for (const sel of selectors) {
    const el = section.querySelector<HTMLElement>(sel);
    if (el && el.getBoundingClientRect().height > 100) {
      return el;
    }
  }
  return null;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

/**
 * Simple word wrap for canvas 2D. Draws up to maxLines lines and appends an
 * ellipsis if more text remains. Advances y by lineHeight per line.
 */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines: number
) {
  const words = text.split(/\s+/);
  let line = '';
  let drawn = 0;
  for (let i = 0; i < words.length; i++) {
    const test = line ? line + ' ' + words[i] : words[i];
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, y + drawn * lineHeight);
      drawn++;
      line = words[i];
      if (drawn === maxLines - 1) {
        // Last allowed line: append remaining words with an ellipsis if
        // they don't fit.
        const rest = words.slice(i).join(' ');
        let candidate = rest;
        while (
          candidate.length > 0 &&
          ctx.measureText(candidate + '\u2026').width > maxWidth
        ) {
          candidate = candidate.slice(0, -1);
        }
        ctx.fillText(
          candidate.length < rest.length ? candidate + '\u2026' : candidate,
          x,
          y + drawn * lineHeight
        );
        return;
      }
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, y + drawn * lineHeight);
}
